import React, {useEffect, useState } from "react";
import {useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import PageActions from "../../../../base/PageActions";
import ShareButton from "../../../../../components/share-button/ShareButton";
import ContentBox from "../../../../../components/content-box/ContentBox";
import {useEnvironment} from "../../../../../hooks/UseEnvironment";
import _image_service from "../../../../../services/ImageService";
import '../../../../styles/Animation.scss';
import '../../../../styles/Common.scss';
import '../../../../styles/Form.scss';
import '../TfGeneratorPage.scss';
import './TfGeneratorResultPage.scss';
import { TfGeneratorPageContext } from "../TfGeneratorPage";

const TfGeneratorResultPage = () => {

    const _target_client = useEnvironment().targetClient;

    const navigate = useNavigate();

    const context = useOutletContext<TfGeneratorPageContext>();

    const [params] = useSearchParams();

    const [isAnimating, setAnimating] = useState<boolean>(true);

    useEffect(() => {
        setTimeout(() => {
            setAnimating(false);
        }, 1500);
    }, []);

    if (!context) {
        return null;
    }
    const { result, query, getGeneratorResult, getQRResult } = context;
    const resultData = result as typeof result | null;
    const hasSuccessfulResult = !!resultData && resultData.success;
    const hasNoResult = resultData === null || (!!resultData && !resultData.success);

    if (params.get('qr') === 'true' && resultData === undefined) {
        getQRResult();
    } else if (resultData === undefined) {
        navigate("/tf-generator", { replace: true });
        return null;
    }

    const page: PageActions = new PageActions({
        title: `Random Transformation Generator - Chase the Fox`,
        description: `Feel like changing for a change? What will Chase zap you into next?`,
        keywords: ["TF Generator", "transformation generator", "transformation randomizer", "TF randomizer"],
        imgSrc: `chase/assets/tf_victim.png`,
        url: `tf-generator`
    });

    const getFullCreatureName = (): string => {

        if (!hasSuccessfulResult || !resultData) {
            return "";
        }

        const { subName, name, modifier, mythical, casarokanUnique } = resultData;

        let fullName = name;

        if (subName) {
            fullName = `${subName} ${fullName}`;
        }

        if (mythical) {
            fullName = `Mythical ${fullName}`;
        } else if (casarokanUnique) {
            fullName = `Casarokan ${fullName}`;
        }

        if (modifier) {
            fullName = `${modifier} ${fullName} `;
        }

        return fullName;
    }

    const getSocialMediaText = (): string => {
        return `Oh no! Chase the Fox transformed me into a ${getFullCreatureName()}! What will he change you 
                into? ${_target_client}tf-generator`;
    }

    const handleReroll = () => {
        if (hasNoResult) {
            return;
        }
        setAnimating(true);
        getGeneratorResult(query);
        setTimeout(() => {
            setAnimating(false);
        }, 1500);
    }

    const renderResult = (): JSX.Element => {
        return (
            <div className="result-display fade-in">
                <h2 className="headline center">
                    {hasSuccessfulResult ? `...${getFullCreatureName()}!` : "Oops! I guess I missed..."}
                </h2>
                <div className="tf-generator-layout">
                    <div className="tf-generator-controls">
                        <p className="result-message center">
                            {hasSuccessfulResult
                                ? "It could have been worse, right? You can always try again!"
                                : "No creature of that requested type could be found. It may have been a magical misfire."}
                        </p>
                        <div className="tf-generator-buttons">
                            <input className="form-button-primary"
                                   type="button"
                                   value={hasSuccessfulResult ? "Transform Again!" : "Transform Again (Unavailable)"}
                                   onClick={handleReroll}
                                   disabled={hasNoResult}/>
                        </div>
                        <p className="result-message center">
                            {hasSuccessfulResult
                                ? "Want a different type of TF? You can always go back!"
                                : "You can go back and try different options."}
                        </p>
                        <div className="tf-generator-buttons">
                            <input className="form-button-secondary"
                                   type="button"
                                   value="Back to Options"
                                   onClick={() => navigate('/tf-generator')}/>
                        </div>
                        {hasSuccessfulResult && (
                            <ul className="result-tweet">
                                <li>
                                    <ShareButton type="Twitter"
                                                    text={getSocialMediaText()}/>
                                </li>
                                <li>
                                    <ShareButton type="Bluesky"
                                                    text={getSocialMediaText()}/>
                                </li>
                                <li>
                                    <ShareButton type="Mastodon"
                                                    text={getSocialMediaText()}/>
                                </li>
                            </ul>
                        )}
                    </div>
                    <div className="tf-generator-image">
                        <img src={_image_service.getCdnImageSrc(hasSuccessfulResult ? 'chase/assets/tf_victim.png' : 'chase/assets/chase_confused.png')}
                             alt={hasSuccessfulResult
                                 ? `This is you. You are now a ${getFullCreatureName()}.`
                                 : "No creature result could be generated."}
                             title={hasSuccessfulResult
                                 ? `This is you. You are now a ${getFullCreatureName()}.`
                                 : "No creature result could be generated."}/>
                    </div>
                </div>
            </div>
        );
    }

    return page.generateMetaContent(
        <>
            <ContentBox className="tf-gen-result">
                {isAnimating
                    ?
                        <>
                            <h1 className="headline">You've been transformed into a... </h1>
                            <div className="result-waiting fade-in-out" hidden={!isAnimating}>
                                <img src={_image_service.getCdnImageSrc('chase/assets/zap.png')}
                                     alt="ZAP!"
                                     title="A brief flash of Chase's magic appears!"/>
                            </div>
                        </>
                    : renderResult()}
            </ContentBox>
        </>
    );
}

export default TfGeneratorResultPage;